import type { RemindKind, ToldPersonDto } from '@nsb/core';
import { dateWords, easternDayOf } from '../days.ts';

/**
 * Who of the client's side was told of a thing waiting on them, in words (design study 26): beside the thing on NSB's
 * own pages, with Remind them. Pure, so the words are tested as written (remind-words.test.ts).
 */

/** "Oct 21", or "today" on the day it is read; the year when it is not this year's. */
const on = (at: string, today: string): string => (easternDayOf(at) === today ? 'today' : dateWords(at, 'dayOrDate', { today }));

const reminded = (p: ToldPersonDto, today: string) => (p.remindedAt ? `, reminded ${on(p.remindedAt, today)}` : '');

/** One person, named, as a thing's line says them; `row` when it follows other words on a row, its first word small. */
function named(p: ToldPersonDto, today: string, row: boolean): string {
  const w = (word: string) => (row ? word.toLowerCase() : word);
  if (p.told === 'emailed') return `${w('Emailed')} to ${p.name} ${on(p.at!, today)}${reminded(p, today)}`;
  if (p.told === 'invited') return `${w('On')} ${p.name}’s Waiting on you since their invitation${reminded(p, today)}`;
  if (p.told === 'waiting') return `${w('Goes')} to ${p.name} by their email setting`;
  if (p.told === 'not_sent') return `${w('Not')} yet sent to ${p.name}: the email did not go`;
  return `${p.name} has no sign-in`;
}

/**
 * Everyone it waits on, one after another: "Emailed to Test Client Oct 21 · Test Partner has no sign-in"; on a row,
 * after its own words, "emailed to Test Client Oct 21 · …" (`row`).
 */
export function toldLine(people: readonly ToldPersonDto[], today: string, opts: { row?: boolean } = {}): string {
  return people.map((p) => named(p, today, opts.row ?? false)).join(' · ');
}

/** One person on a row of their own, their name already beside it (a reviewer): "emailed Oct 8, reminded today". */
export function personLine(p: ToldPersonDto, today: string): string {
  if (p.told === 'emailed') return `emailed ${on(p.at!, today)}${reminded(p, today)}`;
  if (p.told === 'invited') return `on their Waiting on you since their invitation${reminded(p, today)}`;
  if (p.told === 'waiting') return 'goes by their email setting';
  if (p.told === 'not_sent') return 'the email did not go';
  return 'no sign-in';
}

/** Whether Remind them is offered: someone can be reminded now; else whether they were today, and so again tomorrow. */
export function remindState(people: readonly ToldPersonDto[], today: string): 'can' | 'today' | null {
  if (people.some((p) => p.canRemind)) return 'can';
  if (people.some((p) => p.remindedAt && easternDayOf(p.remindedAt) === today)) return 'today';
  return null;
}

const THING: Record<RemindKind, string> = {
  review: 'this review',
  budget: 'this budget',
  step: 'this step',
  deviation: 'confirming this step',
  patterns: 'locking the pattern language',
};

/** "A and B", "A, B and C". */
const and = (names: string[]) => (names.length < 2 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`);

/** The question asked before it goes: "Email Test Client about this budget again, now?" */
export function askLine(kind: RemindKind, people: readonly ToldPersonDto[]): string {
  return `Email ${and(people.filter((p) => p.canRemind).map((p) => p.name))} about ${THING[kind]} again, now?`;
}
